import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Steps, Card, Form, Input, Button, Alert, Radio, Space, Typography, Spin,
  Descriptions, Tag, message, Result,
} from 'antd';
import {
  GoogleOutlined, CloudOutlined, SaveOutlined, CheckCircleOutlined,
  CloseCircleOutlined, DisconnectOutlined, ReloadOutlined, LinkOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const { Title, Text, Paragraph } = Typography;

// ErrorBoundary กันไม่ให้ทั้งแอปเป็นหน้าขาวเมื่อเกิด error ในหน้า Wizard
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || String(error) };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          type="error"
          showIcon
          message="เกิดข้อผิดพลาดในการแสดงหน้า"
          description={this.state.message}
        />
      );
    }
    return this.props.children;
  }
}

const DEFAULT_REDIRECT_URI = `${API_BASE_URL}/api/gdrive/oauth2/callback`;

const GoogleDriveSettings = () => {
  const [searchParams] = useSearchParams();
  const gdriveStatus = searchParams.get('gdrive');
  const gdriveReason = searchParams.get('reason');

  const [current, setCurrent] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingOAuth, setSavingOAuth] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [storageType, setStorageType] = useState('local');
  const [form] = Form.useForm();

  const authToken = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/gdrive/settings`, authToken());
      setSettings(res.data.settings);
      setStorageType(res.data.settings.storageType || 'local');
    } catch (err) {
      message.error('ไม่สามารถดึงการตั้งค่าได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (gdriveStatus === 'success') {
      message.success('เชื่อมต่อ Google Drive สำเร็จแล้ว');
      setCurrent(2);
      fetchSettings();
    } else if (gdriveStatus === 'error') {
      message.error(`เชื่อมต่อ Google Drive ไม่สำเร็จ: ${gdriveReason || ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gdriveStatus]);

  const saveOAuth = async () => {
    try {
      const values = await form.validateFields();
      setSavingOAuth(true);
      await axios.post(`${API_BASE_URL}/api/gdrive/save-oauth`, values, authToken());
      message.success('บันทึกข้อมูล OAuth เรียบร้อยแล้ว');
      await fetchSettings();
      return true;
    } catch (err) {
      message.error(err?.response?.data?.message || 'ไม่สามารถบันทึกข้อมูล OAuth ได้');
      return false;
    } finally {
      setSavingOAuth(false);
    }
  };

  const connectGoogle = async () => {
    const saved = await saveOAuth();
    if (!saved) return;
    setConnecting(true);
    try {
      // ส่ง URL หน้า Wizard ปัจจุบัน (redirect กลับหลัง OAuth)
      const redirect = encodeURIComponent(window.location.href);
      const res = await axios.get(`${API_BASE_URL}/api/gdrive/auth-url?redirect=${redirect}`, authToken());
      window.location.href = res.data.url; // ไปหน้า Google Consent
    } catch (err) {
      message.error('ไม่สามารถเชื่อมต่อ Google ได้');
      setConnecting(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/gdrive/test`, {}, authToken());
      setTestResult(res.data);
      if (res.data.success) {
        message.success(res.data.message);
        await fetchSettings();
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      setTestResult({ success: false, message: err.message });
      message.error('ไม่สามารถทดสอบการเชื่อมต่อได้');
    } finally {
      setTesting(false);
    }
  };

  const activate = async () => {
    setActivating(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/api/gdrive/activate`, { storageType }, authToken());
      message.success(res.data.message);
      await fetchSettings();
    } catch (err) {
      message.error(err?.response?.data?.message || 'ไม่สามารถเปลี่ยน Storage ได้');
    } finally {
      setActivating(false);
    }
  };

  const disconnect = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/gdrive/disconnect`, {}, authToken());
      message.success(res.data.message);
      setStorageType('local');
      await fetchSettings();
    } catch (err) {
      message.error('ไม่สามารถยกเลิกการเชื่อมต่อได้');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const stepItems = [
    { title: 'ข้อมูลเบื้องต้น' },
    { title: 'ตั้งค่า OAuth' },
    { title: 'ทดสอบการเชื่อมต่อ' },
    { title: 'เปิดใช้งาน' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <CloudOutlined /> ตั้งค่า Google Drive Storage
      </Title>
      <Text type="secondary">
        ตั้งค่าการจัดเก็บไฟล์ผ่าน Google Drive (OAuth 2.0) — หากยังไม่ได้ตั้งค่า ระบบจะใช้ Local Storage เป็นค่าเริ่มต้น
      </Text>

      {gdriveStatus === 'success' && (
        <Alert
          style={{ marginTop: 16 }}
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message="เชื่อมต่อ Google Drive สำเร็จแล้ว"
        />
      )}

      <ErrorBoundary>
        <Card style={{ marginTop: '24px' }}>
          <Steps current={current} items={stepItems} onChange={setCurrent} style={{ marginBottom: 24 }} />

        {/* ===== Step 0: ข้อมูลเบื้องต้น ===== */}
        {current === 0 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="ข้อกำหนดเบื้องต้น"
              description="ต้องมีบัญชี Google และสิทธิ์สร้าง OAuth Client ใน Google Cloud Console ก่อน"
            />
            <div>
              <Text strong>วิธีเตรียมความพร้อม:</Text>
              <ol style={{ marginTop: 8, lineHeight: 2 }}>
                <li>เข้า <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">Google Cloud Console</a> และสร้าง Project (หรือใช้ Project เดิม)</li>
                <li>เปิดใช้งาน <b>Google Drive API</b> (APIs &amp; Services {'>'} Library {'>'} ค้นหา "Google Drive API" {'>'} Enable)</li>
                <li>ไปที่ <b>APIs &amp; Services {'>'} OAuth consent screen</b> ตั้งค่า App name และเลือก scope เป็น "External" หรือ "Internal"</li>
                <li>ไปที่ <b>Credentials {'>'} Create Credentials {'>'} OAuth client ID</b> เลือก Application type = <b>Web application</b></li>
                <li>เพิ่ม <b>Authorized redirect URIs</b> ตามด้านล่าง แล้วกด Create</li>
                <li>คัดลอก <b>Client ID</b> และ <b>Client secret</b> ที่ได้</li>
              </ol>
            </div>
            <div>
              <Text strong>Redirect URI ที่ต้องใส่ใน Google Cloud Console:</Text>
              <Paragraph copyable code style={{ marginTop: 8 }}>
                {settings?.redirectUri || DEFAULT_REDIRECT_URI}
              </Paragraph>
            </div>
            <Alert
              type="warning"
              showIcon
              message="คำแนะนำ"
              description="ระบบจะขอสิทธิ์เฉพาะไฟล์ที่แอปสร้าง (drive.file) — ไฟล์อื่นใน Google Drive ของคุณจะไม่ถูกเข้าถึง"
            />
            <Button type="primary" onClick={() => setCurrent(1)}>
              ถัดไป
            </Button>
          </Space>
        )}

        {/* ===== Step 1: ตั้งค่า OAuth ===== */}
        {current === 1 && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{ redirectUri: settings?.redirectUri || DEFAULT_REDIRECT_URI }}
          >
            <Form.Item
              name="clientId"
              label="Google Client ID"
              rules={[{ required: true, message: 'กรุณากรอก Client ID' }]}
            >
              <Input prefix={<GoogleOutlined />} placeholder="xxxxxxxx.apps.googleusercontent.com" />
            </Form.Item>
            <Form.Item
              name="clientSecret"
              label="Google Client Secret"
              rules={[{ required: true, message: 'กรุณากรอก Client Secret' }]}
            >
              <Input.Password placeholder="GOCSPX-..." />
            </Form.Item>
            <Form.Item
              name="redirectUri"
              label="Redirect URI"
              rules={[{ required: true, message: 'กรุณากรอก Redirect URI' }]}
            >
              <Input prefix={<LinkOutlined />} />
            </Form.Item>
            <Space>
              <Button type="primary" icon={<SaveOutlined />} loading={savingOAuth} onClick={saveOAuth}>
                บันทึกการตั้งค่า
              </Button>
              <Button
                type="primary"
                danger
                icon={<GoogleOutlined />}
                loading={connecting}
                onClick={connectGoogle}
              >
                เชื่อมต่อกับ Google
              </Button>
            </Space>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                กด "เชื่อมต่อกับ Google" เพื่อไปยืนยันตัวตนและอนุญาตการเข้าถึง — ระบบจะ redirect กลับมาอัตโนมัติ
              </Text>
            </div>
          </Form>
        )}


        {/* ===== Step 2: ทดสอบการเชื่อมต่อ ===== */}
        {current === 2 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {settings?.connected ? (
              <>
                <Descriptions bordered column={1} size="small" style={{ maxWidth: 500 }}>
                  <Descriptions.Item label="สถานะ">
                    <Tag color="green">เชื่อมต่อแล้ว</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="บัญชี Google">
                    {settings.accountEmail || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="โฟลเดอร์เก็บไฟล์">
                    {settings.folderName || 'e-Sangkha-Storage'}{settings.folderId ? ` (${settings.folderId})` : ''}
                  </Descriptions.Item>
                </Descriptions>

                <Space>
                  <Button icon={<ReloadOutlined />} loading={testing} onClick={testConnection}>
                    ทดสอบการเชื่อมต่อ (อัปโหลด/ลบไฟล์ทดสอบ)
                  </Button>
                  <Button danger icon={<DisconnectOutlined />} onClick={disconnect}>
                    ยกเลิกการเชื่อมต่อ
                  </Button>
                </Space>

                {testResult && (
                  testResult.success ? (
                    <Alert
                      type="success"
                      showIcon
                      icon={<CheckCircleOutlined />}
                      message="เชื่อมต่อสำเร็จ"
                      description={testResult.message}
                    />
                  ) : (
                    <Alert
                      type="error"
                      showIcon
                      icon={<CloseCircleOutlined />}
                      message="เชื่อมต่อไม่สำเร็จ"
                      description={testResult.message}
                    />
                  )
                )}

                <Button type="primary" onClick={() => setCurrent(3)}>
                  ถัดไป
                </Button>
              </>
            ) : (
              <Result
                status="warning"
                title="ยังไม่ได้เชื่อมต่อ Google Drive"
                subTitle={'กรุณากลับไปที่ขั้นตอน "ตั้งค่า OAuth" เพื่อเชื่อมต่อก่อน'}
                extra={
                  <Button type="primary" onClick={() => setCurrent(1)}>
                    ไปขั้นตอนตั้งค่า OAuth
                  </Button>
                }
              />
            )}
          </Space>
        )}

        {/* ===== Step 3: เปิดใช้งาน ===== */}
        {current === 3 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Text strong>เลือก Storage ที่ต้องการใช้สำหรับจัดเก็บไฟล์:</Text>
            <Radio.Group value={storageType} onChange={(e) => setStorageType(e.target.value)}>
              <Radio.Button value="local">Local Storage</Radio.Button>
              <Radio.Button value="googledrive" disabled={!settings?.connected}>
                Google Drive
              </Radio.Button>
            </Radio.Group>
            {storageType === 'googledrive' && !settings?.connected && (
              <Alert type="warning" showIcon message="กรุณาเชื่อมต่อ Google Drive ก่อนเปิดใช้งาน" />
            )}
            {settings?.storageType === 'googledrive' && (
              <Alert type="info" showIcon message="ขณะนี้ระบบกำลังใช้ Google Drive สำหรับจัดเก็บไฟล์" />
            )}
            {settings?.storageType === 'local' && (
              <Alert type="info" showIcon message="ขณะนี้ระบบใช้ Local Storage เป็นค่าเริ่มต้น (Fallback)" />
            )}
            <Space>
              <Button type="primary" icon={<SaveOutlined />} loading={activating} onClick={activate}>
                บันทึกและเปิดใช้งาน
              </Button>
            </Space>
            <Text type="secondary">
              * หาก Google Drive ล้มเหลวระหว่างอัปโหลด ระบบจะ Fallback ไปใช้ Local Storage อัตโนมัติ
            </Text>
          </Space>
        )}
        </Card>
      </ErrorBoundary>
    </div>
  );
};

export default GoogleDriveSettings;

